import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const categories = [
  { labelKey: 'categories.shoes', icon: 'ri-footprint-line', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { labelKey: 'categories.hoodies', icon: 'ri-shirt-line', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { labelKey: 'categories.tshirts', icon: 'ri-t-shirt-line', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { labelKey: 'categories.jackets', icon: 'ri-t-shirt-2-line', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { labelKey: 'categories.pants', icon: 'ri-archive-line', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { labelKey: 'categories.headwear', icon: 'ri-shield-user-line', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { labelKey: 'categories.sets', icon: 'ri-stack-line', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { labelKey: 'categories.underwear', icon: 'ri-user-heart-line', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { labelKey: 'categories.jersey', icon: 'ri-medal-line', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { labelKey: 'categories.accessories', icon: 'ri-brush-2-line', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { labelKey: 'categories.bags', icon: 'ri-shopping-bag-3-line', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { labelKey: 'categories.belts', icon: 'ri-links-line', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { labelKey: 'categories.perfumes', icon: 'ri-drop-line', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { labelKey: 'categories.other', icon: 'ri-more-2-line', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'da', label: 'Dansk' },
  { code: 'de', label: 'Deutsch' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const handleLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const handleCategory = (url: string) => {
    setCatOpen(false);
    setMobileOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200 shadow-sm">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            className="flex items-center gap-2 font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight cursor-pointer"
          >
            <span className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-white">
              <i className="ri-grid-line text-lg" />
            </span>
            <span className="whitespace-nowrap">AllChinaBuy</span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap"
            >
              {t('nav.home')}
            </a>
            <a
              href="/allchinabuy-spreadsheet/"
              onClick={(e) => { e.preventDefault(); navigate('/allchinabuy-spreadsheet/'); }}
              className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap"
            >
              {t('nav.spreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-book-2-line" />
              {t('nav.shopping_guide')}
            </a>

            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap"
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-background-200 py-2 animate-slide-down max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.labelKey}
                      onClick={() => handleCategory(cat.url)}
                      className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 flex-shrink-0">
                        <i className={cat.icon} />
                      </span>
                      {t(cat.labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap"
              >
                <i className="ri-global-line" />
                {languages.find((l) => l.code === i18n.language)?.label || 'English'}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-background-200 py-2 animate-slide-down">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-background-100 ${
                        i18n.language === lang.code ? 'text-primary-600 font-semibold' : 'text-foreground-800'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('cta.button_main')}
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-foreground-900"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="space-y-1 mb-4">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); setMobileOpen(false); }}
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-md"
            >
              <i className="ri-home-5-line" />
              {t('nav.home')}
            </a>
            <a
              href="/allchinabuy-spreadsheet/"
              onClick={(e) => { e.preventDefault(); navigate('/allchinabuy-spreadsheet/'); setMobileOpen(false); }}
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-md"
            >
              <i className="ri-grid-line" />
              {t('nav.spreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-md"
            >
              <i className="ri-book-2-line" />
              {t('nav.shopping_guide')}
            </a>
          </div>

          <p className="text-xs font-bold uppercase tracking-wider text-foreground-500 mb-2">
            {t('nav.categories')}
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {categories.map((cat) => (
              <button
                key={cat.labelKey}
                onClick={() => handleCategory(cat.url)}
                className="flex items-center gap-2 px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-left text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded bg-white text-foreground-600 flex-shrink-0">
                  <i className={cat.icon} />
                </span>
                <span className="truncate">{t(cat.labelKey)}</span>
              </button>
            ))}
          </div>

          <p className="text-xs font-bold uppercase tracking-wider text-foreground-500 mb-2">
            {t('nav.language')}
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguage(lang.code)}
                className={`px-3 py-2 text-xs font-semibold rounded-md border transition-colors ${
                  i18n.language === lang.code
                    ? 'border-primary-500 text-primary-600 bg-primary-50'
                    : 'border-background-200 text-foreground-700 hover:bg-background-100'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            {t('cta.button_main')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      )}
    </header>
  );
}